import { makeAutoObservable, runInAction } from 'mobx';
import { utils, uriToCamel, uuid } from '@intrasync/utils';
import RootStore from '../RootStore';
import ServiceStore from 'stores/ServiceStore';
import { Service } from '@feathersjs/feathers';

const LIMIT = 1000;

interface StorageItem {
  id: string;
  [key: string]: unknown;
}
interface WorkerResponse {
  error?: string;
  workerId?: string;
  requestId?: string;
  workerResult?: StorageItem[] | 'Request came back empty';
}

interface Request {
  service: string;
  checkStorage: boolean;
  query: Record<string, unknown>;
  requestId: string;
  workerId: string;
  startRequest: number;
  options: FindAllOptions;
  completed: boolean;
  syncedItems?: number;
}

interface CustomWorker {
  onmessage: (e: MessageEvent) => void;
  onerror: (e: ErrorEvent) => void;
  onmessageerror: (e: MessageEvent) => void;
  postMessage: (message: unknown) => void;
  terminate: () => void;
}

interface LoopWorkerRequest {
  service: string;
  checkStorage: boolean;
  query: Record<string, unknown>;
  requestId: string;
  workerId: string;
  startRequest: number;
  options: FindAllOptions;
  completed: boolean;
}

interface PostMessageData {
  auth: {
    token: string;
  };
  request: LoopWorkerRequest;
  mode: string;
}

interface FindAllUpdatesOptions {
  reportTime?: boolean;
  reportService?: boolean;
}

interface FindAllUpdatesQuery {
  [key: string]: unknown;
}

interface FindAllUpdatesService {
  find: (params: { query: FindAllUpdatesQuery }) => Promise<{ total: number }>;
}

interface FindAllOptions {
  reportTime?: boolean;
  reportService?: boolean;
}

interface FindAllQuery {
  [key: string]: unknown;
}

class FindAllStore {
  workerError: Error | null = null;
  rootStore: RootStore;
  options: unknown;
  allWorkers: Record<string, CustomWorker> = {};
  requestQueue: Record<string, Request> = {};
  debugMode: boolean = false;

  constructor(rootStore: RootStore, options: unknown) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
    this.options = options;
  }
  handleDebugMode = () => {
    this.debugMode = !this.debugMode;
  };

  createWorker = (workerId: string): CustomWorker | Error | string => {
    if (!window.Worker) {
      console.log('Error Creating Worker');
      if (this.rootStore.authStore?.currentUser?.department === 'Developer') {
        this.rootStore.notificationStore?.addNotification({
          message: `Error Creating Worker - Worker API Unavailable`,
        });
      }
      (this.rootStore.logStore as ServiceStore)
        ?.create({
          message: 'Error Creating Worker',
          level: 'error',
          payload: { message: 'Worker was not available' },
        })
        .catch((err: Error) => {
          console.log('Error', err);
        });
      return new Error('Requires a browser with worker');
    }
    const workerResult = new Worker(
      new URL('./findAllItemsWorker.ts?worker&inline', import.meta.url),
      { type: 'module' }
    );
    if (workerResult) {
      this.setWorker(workerResult as unknown as CustomWorker, workerId);
      return workerResult as unknown as CustomWorker;
    }
    return 'Failed';
  };
  setWorker = (newWorker: CustomWorker, workerId: string) => {
    if (newWorker && workerId) {
      this.allWorkers = { ...this.allWorkers, [workerId]: newWorker };
      this.addWorkerListener(newWorker);
    }
  };
  addWorkerListener = (currentWorker: CustomWorker) => {
    if (!currentWorker) throw Error('No worker');
    currentWorker.onmessage = (e) => {
      if (this.debugMode)
        console.log('Main received a message back from worker');
      if (this.debugMode) console.log('Message:', e);
      if (e.data) {
        return this.setWorkerResponse(JSON.parse(e.data));
      }
      console.log('Response did not have data', e);
    };
    currentWorker.onerror = (e) => {
      if (this.debugMode)
        console.warn('Main received an error back from worker');
      console.warn('Error:', e);
      if (e.message) {
        this.workerError = new Error(e.message);
        return;
      }
    };
    currentWorker.onmessageerror = (e) => {
      console.warn('Main received an message error back from worker');
      console.warn('Error:', e);
      if (e.data) {
        this.workerError = new Error(e.data);
        return;
      }
    };
  };

  createLoopWorkers = (
    service: string,
    query: Record<string, unknown>,
    options: FindAllOptions,
    total: number,
    checkStorage: boolean
  ): LoopWorkerRequest[] => {
    const token = this.rootStore.authStore?.token;
    const loopCount = Math.ceil(total / LIMIT);
    console.log('loopCount', loopCount);
    if (loopCount === 0 || isNaN(loopCount)) {
      return [];
    }

    const looper = Array.from(Array(loopCount).keys());
    return looper.map((run) => {
      const requestId = uuid();
      const workerId = uuid();

      const currentWorker = this.createWorker(workerId) as CustomWorker;
      const currentRequest: LoopWorkerRequest = {
        service: service,
        checkStorage: checkStorage,
        query: {
          $limit: LIMIT,
          $skip: LIMIT * run,
          ...query,
        },
        requestId: requestId,
        workerId: workerId,
        startRequest: new Date().valueOf(),
        options: options,
        completed: false,
      };
      if (this.debugMode) console.log('currentRequest', currentRequest);
      const postMessageData: PostMessageData = {
        auth: {
          token: token || '',
        },
        request: currentRequest,
        mode: this.rootStore.mode,
      };
      currentWorker.postMessage(postMessageData);
      this.addToRequestQueue(currentRequest);
      return currentRequest;
    });
  };

  findAllUpdates = async (
    service: string,
    query: FindAllUpdatesQuery = {},
    options: FindAllUpdatesOptions = {}
  ): Promise<LoopWorkerRequest[]> => {
    if (this.debugMode) console.log('service', service);
    if (this.debugMode) console.log('query', query);

    const currentService: FindAllUpdatesService =
      this.rootStore.app.service(service);

    const { total } = await currentService.find({
      query: { $limit: 0, ...query },
    });
    if (total > 4000) {
      console.group('FindAll Warning');
      console.warn(
        `FindAll doing a really large find (iterating ${total} items), add a query`
      );
      console.warn('service', service);
      console.warn('query', query);
      console.warn('options', options);
      console.groupEnd();
    }
    if (this.debugMode) console.log('LIMIT', LIMIT);
    if (this.debugMode) console.log('total over limit', service, total);

    let checkStorage = false;
    const storeName = `${uriToCamel(service)}Store`;
    if ((this.rootStore[storeName] as { storage?: unknown })?.storage) {
      checkStorage = true;
    }

    return this.createLoopWorkers(
      service,
      query || {},
      options,
      total,
      checkStorage
    );
  };

  findAll = async (
    service: string,
    query: FindAllQuery,
    options: FindAllOptions = {}
  ): Promise<LoopWorkerRequest[]> => {
    if (this.debugMode) console.log('service', service);
    if (this.debugMode) console.log('query', query);

    const currentService = this.rootStore.app.service(service) as Service;

    const { total } = await currentService.find({
      query: { $limit: 0, ...query },
    });
    if (total > 4000) {
      console.group('FindAll Warning');
      console.warn(
        `FindAll doing a really large find (iterating ${total} items), add a query`
      );
      console.warn('service', service);
      console.warn('query', query);
      console.warn('options', options);
      console.groupEnd();
    }
    if (this.debugMode) console.log('LIMIT', LIMIT);
    if (this.debugMode) console.log('total over limit', service, total);

    const checkStorage = false;

    return this.createLoopWorkers(service, query, options, total, checkStorage);
  };

  addToRequestQueue = (newRequest: Request) => {
    this.requestQueue = {
      ...this.requestQueue,
      [newRequest.requestId]: { ...newRequest },
    };
  };

  setWorkerResponse = async (workerResponse: WorkerResponse) => {
    if (this.debugMode) console.log('workerResponse', workerResponse);
    if (workerResponse.error) {
      if (workerResponse.workerId) {
        console.error('Killing worker that had error', workerResponse.workerId);
        this.destroyWorker(workerResponse.workerId);
      }
      return console.error('Something went wrong:', workerResponse.error);
    }
    if (!workerResponse.requestId) {
      return console.error('Something went wrong:', workerResponse);
    }
    const foundRequest = this.requestQueue[workerResponse.requestId];
    if (!workerResponse.workerResult || !workerResponse.workerResult.length) {
      if (this.debugMode) console.log('Worker had no updates', workerResponse);
      if (workerResponse.requestId) {
        this.requestQueue[workerResponse.requestId] = {
          ...foundRequest,
          completed: true,
        };
      }
      if (workerResponse.workerId) {
        return this.destroyWorker(workerResponse.workerId);
      }
    }
    if (foundRequest) {
      if (this.debugMode) console.log('foundRequest', foundRequest);

      const { service } = foundRequest;
      const storeName = `${uriToCamel(service)}Store`;
      if (this.debugMode) console.log('storeName', storeName);

      if (
        this.rootStore[storeName] &&
        workerResponse.workerResult &&
        workerResponse.workerResult === 'Request came back empty'
      ) {
        console.log('Worker result was empty');
        this.requestQueue[workerResponse.requestId] = {
          ...foundRequest,
          completed: true,
        };
        if (workerResponse.workerId) {
          return this.destroyWorker(workerResponse.workerId);
        }
      }

      if (
        this.rootStore[storeName] &&
        workerResponse.workerResult &&
        workerResponse.workerResult.length
      ) {
        console.log('workerResponse.workerResult', workerResponse.workerResult);
        const currentStore = this.rootStore[storeName] as ServiceStore;

        if (Array.isArray(workerResponse.workerResult)) {
          currentStore.mergeItems(workerResponse.workerResult as StorageItem[]);
        }

        if (currentStore?.storage) {
          Array.isArray(workerResponse.workerResult) &&
            workerResponse.workerResult.map((item: { id: string }) => {
              if (currentStore.storage) {
                return currentStore.storage
                  .setItem(`${item.id}`, { ...item })
                  .catch((err: Error) => console.warn('Error', err));
              }
              return Promise.resolve();
            });
        }

        if (foundRequest?.options?.reportTime) {
          const completeRequest = new Date().valueOf();
          const processTime = completeRequest - foundRequest.startRequest;
          if (this.debugMode)
            console.log(
              `${foundRequest.service} took ${processTime}ms to get ${workerResponse.workerResult.length} items`
            );
          if (this.rootStore.notificationStore) {
            const { notificationStore } = this.rootStore;
            notificationStore.addNotification({
              message: `Updated ${workerResponse.workerResult.length} items for ${storeName} in ${processTime}ms`,
              options: { variant: 'Success', autoHideduration: 3000 },
            });
          }
        } else if (foundRequest?.options?.reportService) {
          if (this.rootStore.notificationStore) {
            const { notificationStore } = this.rootStore;
            notificationStore.addNotification({
              message: `Updated ${workerResponse.workerResult.length} items for ${service}`,
              options: { variant: 'Success', autoHideduration: 3000 },
            });
          }
        } else if (this.debugMode) {
          const completeRequest = new Date().valueOf();
          const processTime = completeRequest - foundRequest.startRequest;

          console.log(
            `${foundRequest.service} took ${processTime}ms to get ${workerResponse.workerResult.length} items`
          );
        }
      }
      runInAction(() => {
        if (workerResponse.requestId) {
          console.log(
            'this.requestQueue[workerResponse.requestId] before',
            this.requestQueue[workerResponse.requestId]
          );
          this.requestQueue[workerResponse.requestId] = {
            ...foundRequest,
            completed: true,
            syncedItems: workerResponse?.workerResult?.length || 0,
          };
          console.log(
            'this.requestQueue[workerResponse.requestId] after',
            this.requestQueue[workerResponse.requestId]
          );
        }
      });

      if (workerResponse.workerId) {
        this.destroyWorker(workerResponse.workerId);
      }
    } else {
      workerResponse.workerId && this.destroyWorker(workerResponse.workerId);
    }
  };

  destroyWorker = (workerId: string): void => {
    if (this.debugMode) console.log('destroyWorker workerId', workerId);
    const foundWorker = this.allWorkers[workerId];
    if (foundWorker) {
      if (this.debugMode) console.log('Terminating worker', foundWorker);
      foundWorker.terminate();
      delete this.allWorkers[workerId];

      if (this.debugMode)
        console.log('Terminated worker, remaining workers', {
          ...this.allWorkers,
        });
    }
  };
  setWorkerError = (workerError: ErrorEvent | string) => {
    if (this.debugMode) console.log('workerError', workerError);
    this.workerError =
      workerError instanceof ErrorEvent
        ? new Error(workerError.message)
        : new Error(workerError);
  };
}

export default FindAllStore;
